/**
 * Global State Management using React Context
 * for AI Algo Trading Application
 */

import { createContext, useContext, useReducer, useCallback, ReactNode } from 'react'

// Types
export interface Algorithm {
  id: string
  name: string
  description: string
  strategy: string
  status: 'active' | 'inactive' | 'testing'
  parameters: AlgorithmParameters
  riskManagement: RiskManagement
  performance: PerformanceMetrics
}

export interface AlgorithmParameters {
  entryConditions: string[]
  exitConditions: string[]
  positionSize: number
  maxPositions: number
  stopLoss: number
  takeProfit: number
  trailingStop?: number
}

export interface RiskManagement {
  maxDrawdown: number
  dailyLossLimit: number
  maxRiskPerTrade: number
  emergencyStopEnabled: boolean
}

export interface PerformanceMetrics {
  totalReturn: number
  winRate: number
  profitFactor: number
  sharpeRatio: number
  maxDrawdown: number
  totalTrades: number
  avgWin: number
  avgLoss: number
}

export interface Backtest {
  id: string
  algorithmId: string
  name: string
  date: string
  status: 'running' | 'completed' | 'failed'
  results?: BacktestResults
  configuration: BacktestConfig
}

export interface BacktestConfig {
  symbol: string
  startDate: string
  endDate: string
  initialCapital: number
  commission: number
  slippage: number
}

export interface BacktestResults {
  finalCapital: number
  totalReturn: number
  totalReturnPercent: number
  winRate: number
  profitFactor: number
  maxDrawdown: number
  sharpeRatio: number
  totalTrades: number
  equityCurve: { date: string; value: number }[]
  trades: Trade[]
}

export interface Trade {
  id: string
  symbol: string
  type: 'long' | 'short'
  entryPrice: number
  exitPrice?: number
  quantity: number
  entryDate: string
  exitDate?: string
  pnl?: number
  status: 'open' | 'closed'
}

export interface Position {
  id: string
  symbol: string
  quantity: number
  avgPrice: number
  currentPrice: number
  pnl: number
  pnlPercent: number
}

// State Actions
type Action =
  | { type: 'SET_ALGORITHMS'; payload: Algorithm[] }
  | { type: 'ADD_ALGORITHM'; payload: Algorithm }
  | { type: 'UPDATE_ALGORITHM'; payload: { id: string; updates: Partial<Algorithm> } }
  | { type: 'DELETE_ALGORITHM'; payload: string }
  | { type: 'SET_ACTIVE_ALGORITHM'; payload: string | null }
  | { type: 'START_BACKTEST'; payload: Backtest }
  | { type: 'UPDATE_BACKTEST'; payload: { id: string; updates: Partial<Backtest> } }
  | { type: 'SET_POSITIONS'; payload: Position[] }
  | { type: 'UPDATE_POSITION'; payload: Position }
  | { type: 'SET_TRADING_ENABLED'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }

// State Interface
interface AppState {
  algorithms: Algorithm[]
  activeAlgorithmId: string | null
  backtests: Backtest[]
  positions: Position[]
  tradingEnabled: boolean
  loading: boolean
  error: string | null
}

const initialState: AppState = {
  algorithms: [],
  activeAlgorithmId: null,
  backtests: [],
  positions: [],
  tradingEnabled: false,
  loading: false,
  error: null,
}

// Reducer
function appReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_ALGORITHMS':
      return { ...state, algorithms: action.payload }
    case 'ADD_ALGORITHM':
      return { ...state, algorithms: [...state.algorithms, action.payload] }
    case 'UPDATE_ALGORITHM':
      return {
        ...state,
        algorithms: state.algorithms.map((algo) =>
          algo.id === action.payload.id ? { ...algo, ...action.payload.updates } : algo
        ),
      }
    case 'DELETE_ALGORITHM':
      return {
        ...state,
        algorithms: state.algorithms.filter((algo) => algo.id !== action.payload),
      }
    case 'SET_ACTIVE_ALGORITHM':
      return { ...state, activeAlgorithmId: action.payload }
    case 'START_BACKTEST':
      return { ...state, backtests: [...state.backtests, action.payload] }
    case 'UPDATE_BACKTEST':
      return {
        ...state,
        backtests: state.backtests.map((bt) =>
          bt.id === action.payload.id ? { ...bt, ...action.payload.updates } : bt
        ),
      }
    case 'SET_POSITIONS':
      return { ...state, positions: action.payload }
    case 'UPDATE_POSITION':
      return {
        ...state,
        positions: state.positions.some((p) => p.id === action.payload.id)
          ? state.positions.map((p) => (p.id === action.payload.id ? action.payload : p))
          : [...state.positions, action.payload],
      }
    case 'SET_TRADING_ENABLED':
      return { ...state, tradingEnabled: action.payload }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    default:
      return state
  }
}

// Context
const AppContext = createContext<{
  state: AppState
  dispatch: React.Dispatch<Action>
} | null>(null)

// Provider
export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState)
  return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>
}

// Custom Hook
export function useAppStore() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useAppStore must be used within AppProvider')
  }
  const { state, dispatch } = context

  // Action wrappers for better DX
  const setAlgorithms = useCallback((algorithms: Algorithm[]) => {
    dispatch({ type: 'SET_ALGORITHMS', payload: algorithms })
  }, [])

  const addAlgorithm = useCallback((algorithm: Algorithm) => {
    dispatch({ type: 'ADD_ALGORITHM', payload: algorithm })
  }, [])

  const updateAlgorithm = useCallback((id: string, updates: Partial<Algorithm>) => {
    dispatch({ type: 'UPDATE_ALGORITHM', payload: { id, updates } })
  }, [])

  const deleteAlgorithm = useCallback((id: string) => {
    dispatch({ type: 'DELETE_ALGORITHM', payload: id })
  }, [])

  const setActiveAlgorithm = useCallback((id: string | null) => {
    dispatch({ type: 'SET_ACTIVE_ALGORITHM', payload: id })
  }, [])

  const startBacktest = useCallback((backtest: Backtest) => {
    dispatch({ type: 'START_BACKTEST', payload: backtest })
  }, [])

  const updateBacktest = useCallback((id: string, updates: Partial<Backtest>) => {
    dispatch({ type: 'UPDATE_BACKTEST', payload: { id, updates } })
  }, [])

  const setPositions = useCallback((positions: Position[]) => {
    dispatch({ type: 'SET_POSITIONS', payload: positions })
  }, [])

  const updatePosition = useCallback((position: Position) => {
    dispatch({ type: 'UPDATE_POSITION', payload: position })
  }, [])

  const setTradingEnabled = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_TRADING_ENABLED', payload: enabled })
  }, [])

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading })
  }, [])

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error })
  }, [])

  return {
    state,
    actions: {
      setAlgorithms,
      addAlgorithm,
      updateAlgorithm,
      deleteAlgorithm,
      setActiveAlgorithm,
      startBacktest,
      updateBacktest,
      setPositions,
      updatePosition,
      setTradingEnabled,
      setLoading,
      setError,
    },
  }
}

// Selector Hooks
export function useAlgorithms() {
  const { state } = useAppStore()
  return state.algorithms
}

export function useActiveAlgorithm() {
  const { state } = useAppStore()
  return state.algorithms.find((a) => a.id === state.activeAlgorithmId) || null
}

export function useBacktests() {
  const { state } = useAppStore()
  return state.backtests
}

export function usePositions() {
  const { state } = useAppStore()
  return state.positions
}

export function useTradingState() {
  const { state } = useAppStore()
  return {
    tradingEnabled: state.tradingEnabled,
    loading: state.loading,
    error: state.error,
  }
}
